import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The pack opening, as a clock (SPEC-010 PR-10.4).
 *
 * The third sequence in this folder and the same shape as the other two: a
 * browser timer bound to a named sequence, the sequence being the only
 * interesting part, and the timers injected so a suite can walk it stage by
 * stage rather than wait three seconds per assertion. `useStartLights` is the
 * nearest sibling and this is deliberately its twin.
 *
 * What is different is the FIRST stage. A starting tree begins the moment the
 * reader asks for a race; a pack does not begin at all until it is touched. That
 * wait is the entire mechanism being copied here — the pack sits there being
 * unopened, and the reader decides when to find out. So `sealed` is a resting
 * state with no timer under it, and everything after the tear is on one.
 */

/**
 * The five acts, in order.
 *
 *   sealed       the card back, on the field, waiting to be touched.
 *   tearing      it splits along the ramp.
 *   rarity       the ribbon lands alone on a flare of its own tier. The tension
 *                beat, and the one the whole genre is built on: it is the only
 *                part of a card that can be shown without giving the car away,
 *                so it is the only part that can build to it.
 *   walkout      the car arrives under its own power.
 *   introduced   the card, whole — the figures, the gallery, the specification,
 *                and the two ways out.
 */
export const REVEAL_STAGES = ['sealed', 'tearing', 'rarity', 'walkout', 'introduced'];

/**
 * How long each act is HELD before the next one starts, in the same order.
 *
 * `sealed` is zero because nothing schedules it — the reader does. `introduced`
 * is zero because nothing follows it. The three in between are the ceremony, and
 * they are weighted rather than even: the flare is the longest because
 * anticipation is what it is for, and the tear is the shortest because a seam
 * that lingers reads as a stuck animation rather than as a tear.
 *
 * Two and a half seconds all told. FIFA's is nearer eleven; this one has to be
 * over before it becomes a toll on playing again, which is the same trade
 * `useStartLights` makes with its two-amber tree.
 */
export const STAGE_HOLDS = [0, 560, 900, 1100, 0];

export default function useRevealSequence({
    reducedMotion = false,
    schedule = (callback, delay) => setTimeout(callback, delay),
    cancel = (handle) => clearTimeout(handle),
} = {}) {
    /**
     * A reader who asked for less movement opens at the END of the sequence.
     *
     * Not a faster ceremony — none of it. They did not ask to wait less for the
     * same movement, and there is no reading order in these five acts to
     * preserve: the flare exists to withhold the car, so under this preference
     * it has nothing to do but delay it. The splash makes the identical trade
     * (SPEC-001), and it is the only honest reading of the preference for a
     * sequence whose entire content is suspense.
     */
    const [stage, setStage] = useState(() => (reducedMotion ? 'introduced' : 'sealed'));
    const timer = useRef(null);

    /**
     * The injected timers, held in a ref rather than closed over.
     *
     * `schedule` and `cancel` are DEFAULTED PARAMETERS, which means a caller who
     * passes neither — every caller in production — hands this hook a brand new
     * pair of arrow functions on every render. A `stop` memoised on them would
     * therefore change identity every render, the unmount effect below would
     * re-run its cleanup every render, and the act that was in flight would be
     * cancelled by a re-render that had nothing to do with it.
     *
     * That is not a hypothetical: the duel screen re-renders once a second while
     * its clock runs, and the ceremony opens over the top of it. Holding the
     * pair in a ref is what makes `stop` — and `open` under it — genuinely
     * stable while still resolving the CURRENT functions when one is called.
     */
    const timers = useRef({ schedule, cancel });

    timers.current = { schedule, cancel };

    const stop = useCallback(() => {
        if (timer.current !== null) {
            timers.current.cancel(timer.current);
            timer.current = null;
        }
    }, []);

    /**
     * Straight to the card.
     *
     * Escape reaches this rather than closing the dialog (SPEC-010 D-12): a
     * reader who dismissed a car they had just won would have no way back into
     * the moment, and the ceremony would have taken the prize away.
     */
    const skip = useCallback(() => {
        stop();
        setStage('introduced');
    }, [stop]);

    const open = useCallback(() => {
        stop();

        if (reducedMotion) {
            setStage('introduced');

            return;
        }

        // Each act schedules the next rather than four timers set at once: one
        // chain can be cut with one cancel, and four cannot.
        const advance = (index) => {
            setStage(REVEAL_STAGES[index]);

            if (index === REVEAL_STAGES.length - 1) {
                timer.current = null;

                return;
            }

            timer.current = timers.current.schedule(() => advance(index + 1), STAGE_HOLDS[index]);
        };

        advance(1);
    }, [reducedMotion, stop]);

    useEffect(() => stop, [stop]);

    return {
        stage,
        // The card is on screen and the controls under it can be used. Asked as
        // a question rather than compared to a string at four call sites, so the
        // day a sixth act is added there is one place that decides what "done"
        // means.
        isIntroduced: stage === 'introduced',
        open,
        skip,
    };
}
